<template>
    <div id="loginOverlay" class="overlay">
      <div id="loginModal" class="modal" v-click-outside="toggleLoginModal">
        <div class="modal-header">
          <div id="loginHeader">LOGIN</div>
        </div>
        <div id="loginContent" class="modal-content">
          <div id="loginErrorMessage" v-if="showLoginErrorMessage" v-html="loginErrorMessage"></div>
          <div id="loginForm">
            <div class="login-item" id="principalTypeSelector" v-if="showPrincipalTypeSelector">
              <div class="item-label">Login Type</div>
              <div id="principalTypeToggle">
                <div
                  class="principal-type-option"
                  :class="{ 'principal-type-option-active': userCredentials.principalType === 'individual' }"
                  @click="selectPrincipalType('individual')"
                >Individual</div>
                <div
                  class="principal-type-option"
                  :class="{ 'principal-type-option-active': userCredentials.principalType === 'team' }"
                  @click="selectPrincipalType('team')"
                >Team</div>
              </div>
            </div>
            <div class="login-item">
              <div class="item-label">User</div>
              <SearchDropdown v-model="userCredentials.id" :items="filteredUsers" title="Select User" @update:modelValue="clearError" />
            </div>
            <div class="login-item">
              <div class="item-label">Password</div>
              <input id="userPassword" type="password" v-model="userCredentials.password" placeholder="Password" @input="clearError" @keydown.enter="attemptLogin" />
            </div>
          </div>
          <div id="loginControls">
            <div id="loginButton" class="game-button" :class="{'disabled': loginDisabled}" @click="login(userCredentials)">LOG IN</div>
          </div>
        </div>
      </div>
    </div>
</template>

<script>
import vClickOutside from 'click-outside-vue3'
import SearchDropdown from './SearchDropdown.vue'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'LoginModal',
  components: {
    SearchDropdown
  },
  watch: {
    // Handles a round transition while the modal is open (e.g. the active round switches from
    // individual to team, or from a locked type back to open selection). Re-locks the type and
    // clears any now-invalid user selection instead of leaving a stale, mismatched choice.
    lockedPrincipalType(newLockedType){
      const theLoginModal = this;
      if(newLockedType && theLoginModal.userCredentials.principalType !== newLockedType){
        theLoginModal.selectPrincipalType(newLockedType);
      }
    },
  },
  computed: {
    ...mapGetters([
      'moduleTitle',
      'moduleAssetFolderName',
      'users',
      'rounds',
      'currentRound',
      'showLoginErrorMessage',
      'loginErrorMessage',
    ]),
    // The active round MAY restrict logins to a single principal type (individual or team). When it
    // does, the type toggle is hidden and login is locked to that type, since only one type is eligible.
    activeRound(){
      const theLoginModal = this;
      return theLoginModal.currentRound !== null
        ? theLoginModal.rounds.find(round => round.id === theLoginModal.currentRound)
        : null;
    },
    lockedPrincipalType(){
      const theLoginModal = this;
      return (theLoginModal.activeRound && theLoginModal.activeRound.roundType) || null;
    },
    showPrincipalTypeSelector(){
      const theLoginModal = this;
      return !theLoginModal.lockedPrincipalType;
    },
    simpleUsers(){
      const theLoginModal = this;
      return theLoginModal.users.map(user => {
        return {
          firstName: user.firstName,
          lastName: user.lastName,
          id: user.id,
          principalType: user.userType || 'individual',
        }
      })
    },
    filteredUsers(){
      const theLoginModal = this;
      return theLoginModal.simpleUsers.filter(user => user.principalType === theLoginModal.userCredentials.principalType)
    },
    loginDisabled(){
      const theLoginModal = this;
      return (theLoginModal.userCredentials.id === '' || theLoginModal.userCredentials.password === '' || theLoginModal.showLoginErrorMessage)
    },
  },
  methods: {
    ...mapActions([
      'login',
      'toggleLoginModal',
      'toggleLoginError'
    ]),
    clearError(){
      const theLoginModal = this;
      if(theLoginModal.showLoginErrorMessage){
        theLoginModal.toggleLoginError({message: '', visible: false})
      }
    },
    selectPrincipalType(newType){
      const theLoginModal = this;
      if(theLoginModal.userCredentials.principalType === newType){
        return;
      }
      theLoginModal.userCredentials.principalType = newType;
      // clear any previously selected user, since it may not be valid under the new type filter
      theLoginModal.userCredentials.id = '';
      theLoginModal.clearError();
    },
    attemptLogin(){
      const theLoginModal = this;
      if(!theLoginModal.loginDisabled){
        theLoginModal.login(theLoginModal.userCredentials)
      }
    }
  },
  mounted(){
    const theLoginModal = this;
    if(theLoginModal.lockedPrincipalType){
      theLoginModal.userCredentials.principalType = theLoginModal.lockedPrincipalType;
    }
  },
  beforeUnmount(){

  },
  data: function(){
    return {
      userCredentials: {
        id: '',
        password: '',
        principalType: 'individual'
      }
    }
  },
  directives: {
    clickOutside: vClickOutside.directive
  },
}
</script>

<style>
  #loginOverlay{

  }

  #loginModal{

  }

  #loginHeader{
    
  }

  #loginContent{

  }

  #loginContent.modal-content{
    overflow-y: initial; /* NOTE: THIS IS SET TO auto EVERYWHERE ELSE TO ALLOW FOR SCROLLING; THIS IS DIFFERENT HERE TO ALLOW OVERFLOW OF DROPDOWN AND SCROLL OF ALL USERS  */
  }

  #loginForm{
    width: 80%;
    display: flex;
    flex-direction: column;
    margin: 0 auto;
  }

  #loginErrorMessage{
    font-size: 20px;
    font-weight: bold;
    padding: 10px 0px;
    width: 80%;
    margin: 20px auto 10px auto;
  }

  .login-item{
    margin-top: 15px;
  }

  #principalTypeToggle{
    display: flex;
    flex-direction: row;
    width: 100%;
  }

  .principal-type-option{
    flex: 1;
    text-align: center;
    padding: 8px;
    cursor: pointer;
    border: 1px solid #333;
    font-weight: bold;
  }

  .principal-type-option-active{
    background-color: #333;
    color: #fff;
  }

  .login-item select, .login-item input{
    width: 100%;
    padding: 5px;
    margin: 5px auto;
  }

  .login-item input::placeholder{
    font-weight: bold;
  }

  .item-label{
    text-align: left;
    font-weight: bold;
    font-size: 20px;
  }

  #loginControls{
    width: 100%;
    max-width: 200px;
    margin-top: 20px;
    margin: 10px auto;
  }

  #loginButton{
    font-size: 24px;
    transition: all 0.25s;
  }
  #loginButton:hover{
    cursor: pointer;
  }

  #loginButton.disabled{
    pointer-events: none;
    background-color: rgba(51, 51, 51, 0.4);
    color: rgba(255, 255, 255, 0.4);
  }
</style>